import { Amplify } from "aws-amplify"
import dayjs from "dayjs"
import ja from "dayjs/locale/ja"
import "expo-dev-client"
import { SystemBars } from "react-native-edge-to-edge"
import { memo } from "react"
import { StyleSheet, View } from "react-native"
import "react-native-gesture-handler"
import { GestureHandlerRootView } from "react-native-gesture-handler"
import { SafeAreaProvider } from "react-native-safe-area-context"
import { RecoilRoot } from "recoil"
import { LoadingIndicator } from "src/components/parts/LoadingIndicator"
import useCachedResources from "src/hooks/useCachedResources"
import useColorScheme from "src/hooks/useColorScheme"
import { Navigation } from "src/navigation"
import { crashlyticsWrap } from "src/tags/crashlytics"
import { adWrap } from "src/tags/ads"
import * as Notifications from "expo-notifications"
import { TermsModal } from "src/components/parts/TermsModal"
import { useResetAppBadgeIconEffect } from "src/hooks/useResetAppBadgeIconEffect"
import { useAndroidFirebasePushHandler } from "src/hooks/useAndroidFirebasePushHandler"
import { useIOSWebViewHandler } from "src/hooks/useIOSWebViewHandler"
import { useLastNotificationResponseSafe } from "src/hooks/useLastNotificationResponseSafe"
import { setAsync } from "src/storage/secureStorage/setAsync"
import { useEffect } from "react"
import { Platform } from "react-native"
import * as Linking from "expo-linking"

const config = require("src/aws/aws-exports") // eslint-disable-line
Amplify.configure(config.default)

dayjs.locale(ja)

const RecoilManagedApp = memo(() => {
  const isLoadingComplete = useCachedResources()
  const colorScheme = useColorScheme()
  crashlyticsWrap.log("mounted App")
  adWrap.initialize()

  // Web: 通知機能はサポートされていないため、通知ハンドラーを設定しない
  useEffect(() => {
    if (Platform.OS !== "web") {
      Notifications.setNotificationHandler({
        handleNotification: async () => ({
          shouldShowAlert: true,
          shouldPlaySound: false,
          shouldSetBadge: false,
        }),
      })
    }
  }, [])

  // 通知タップの処理
  const lastNotificationResponse = useLastNotificationResponseSafe()

  // Android: 通常通知（urlScheme）の処理
  useEffect(() => {
    if (Platform.OS === "android" && lastNotificationResponse) {
      const notification = lastNotificationResponse?.notification as Record<string, any> | undefined

      if (notification?.request) {
        // バックグラウンド/終了状態: trigger.remoteMessage.data
        // フォアグラウンド（Firebase Messaging経由）: content.data
        const data = notification.request.trigger?.remoteMessage?.data || notification.request.content?.data

        if (data?.urlScheme) {
          setAsync("urlScheme", data.urlScheme)

          const prefix = Linking.createURL("/")
          Linking.openURL(`${prefix}${data.urlScheme}`)
        }
      }
    }
  }, [lastNotificationResponse])

  useResetAppBadgeIconEffect()
  useAndroidFirebasePushHandler(lastNotificationResponse)
  useIOSWebViewHandler(lastNotificationResponse)

  if (!isLoadingComplete) {
    return (
      <View style={[styles.fullHeight, styles.centering]}>
        <LoadingIndicator />
      </View>
    )
  } else {
    return (
      <GestureHandlerRootView style={styles.fullHeight}>
        <SafeAreaProvider>
          <Navigation colorScheme={colorScheme} />
          <SystemBars />
          <TermsModal />
        </SafeAreaProvider>
      </GestureHandlerRootView>
    )
  }
})

export default function App() {
  return (
    <RecoilRoot>
      <RecoilManagedApp />
    </RecoilRoot>
  )
}

const styles = StyleSheet.create({
  centering: {
    justifyContent: "center",
    alignItems: "center",
  },
  fullHeight: {
    flex: 1,
  },
})
